/* ============================================================
   SINGSPACE ADMIN - PAGE EDITION D'ESPACE (Refactorisée)
   ============================================================ */

import { useNavigate, useParams } from 'react-router-dom';
import { SpaceEditHeader } from '../../components/spaces/SpaceEditHeader';
import { ConfigWizard } from '../../components/spaces/wizard/ConfigWizard';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useImageUpload } from '../../hooks/useImageUpload';
import { useSpaceEditHandlers } from '../../hooks/useSpaceEditHandlers';
import { useSpaceEditor } from '../../hooks/useSpaceEditor';

export function SpaceEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { uploadImage } = useImageUpload();
  const { space, isLoading, error, isDirty, saveWithData, saveDisplay, updateField, reload } = useSpaceEditor(id);

  const { handleStepSave, handleFinish } = useSpaceEditHandlers({
    id: id!,
    saveWithData,
    saveDisplay,
    updateField,
    reload,
    uploadImage,
  });

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} onBack={() => navigate('/spaces')} />;
  if (!space) return <NotFoundState />;

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafb' }}>
      <SpaceEditHeader space={space} isDirty={isDirty} />

      <div style={{ maxWidth: '100%', margin: '0 auto', padding: '2rem' }}>
        <ConfigWizard initialData={space} onStepSave={handleStepSave} onFinish={handleFinish} />
      </div>

      <FooterInfo />
    </div>
  );
}

// --- Sub-components for Clean Code ---

function LoadingState() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafb',
      }}
    >
      <LoadingSpinner message="Chargement de l'espace..." submessage="Veuillez patienter" size="md" />
    </div>
  );
}

function ErrorState({ error, onBack }: { error: string; onBack: () => void }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafb',
        padding: '2rem',
      }}
    >
      <div style={{ maxWidth: '500px', width: '100%' }}>
        <div
          style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '12px',
            padding: '1.5rem',
            color: '#991b1b',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
        <button
          onClick={onBack}
          style={{
            padding: '0.75rem 1.5rem',
            background: '#00758d',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '0.95rem',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Retour à la liste
        </button>
      </div>
    </div>
  );
}

function NotFoundState() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafb',
      }}
    >
      <div style={{ textAlign: 'center', color: '#6b7280' }}>Espace introuvable</div>
    </div>
  );
}

function FooterInfo() {
  return (
    <div
      style={{ padding: '2rem', textAlign: 'center', color: '#666', borderTop: '1px solid #e5e7eb', marginTop: '2rem' }}
    >
      <p style={{ maxWidth: '800px', margin: '0 auto', fontSize: '0.9rem', lineHeight: '1.6' }}>
        <strong>Besoin d'aide ?</strong> Cette page vous permet de configurer tous les aspects d'un espace, étape par
        étape. Chaque bloc du menu de gauche correspond à un paramètre de fonctionnement de votre espace. Cliquez sur
        les étapes et sauvegardez vos configurations au fur et à mesure pour personnaliser l'offre selon vos besoins.
      </p>
    </div>
  );
}
